import { createGlobalStyle, css } from 'styled-components';
import constants, { fontSize } from '../common/theme/constants';
import colors from '../common/theme/colors';
import { getDeviceInfo } from '../common/utils/KitUI';
// NOTES:
// Figuring out how this all works between Storybook and the needs of any React App was borrowed from:
// https://github.com/storybookjs/design-system
// See .storybook/preview.js to see how the global styles and font is loaded for Storybook.
// See cp-tookit README for instructions how to integrate global styles and font loading.

const deviceInfo = getDeviceInfo();

export const KitHtmlStyles = css`
  width: 100%;
  height: 100%;
`;

export const KitBodyStyles = css`
  width: 100%;
  height: 100%;
  font-family: ${({ theme }) => theme.font.fontFamily};
  font-size: ${constants.baseFontSize}px;
  font-weight: ${constants.fontWeight.normal};
  background-color: ${({ theme }) => theme.page.body.bg};
  color: ${({ theme }) => theme.page.body.text};

  margin: 0;
  overflow-y: auto;
  overflow-x: hidden;

  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  -webkit-tap-highlight-color: transparent;
  -webkit-overflow-scrolling: touch;

  * {
    box-sizing: border-box;
    font-size: 1rem;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    padding: 0;
    font-family: ${({ theme }) => theme.headingFont.fontFamily};
    font-weight: ${constants.fontWeight.bold};
    color: ${({ theme }) => theme.page.body.headerText};
  }

  h1 {
    font-size: ${({ theme }) => theme.headingFont.fontSize.h1}rem;
  }
  h2 {
    font-size: ${({ theme }) => theme.headingFont.fontSize.h2}rem;
  }
  h3 {
    font-size: ${({ theme }) => theme.headingFont.fontSize.h3}rem;
  }
  h4 {
    font-size: ${({ theme }) => theme.headingFont.fontSize.h4}rem;
  }
  h5 {
    font-size: ${({ theme }) => theme.headingFont.fontSize.h5}rem;
  }
  h6 {
    font-size: ${({ theme }) => theme.headingFont.fontSize.h6}rem;
  }

  h1:first-child,
  h2:first-child,
  h3:first-child,
  h4:first-child,
  h5:first-child,
  h6:first-child {
    margin-top: 0;
  }

  h1:last-child,
  h2:last-child,
  h3:last-child,
  h4:last-child,
  h5:last-child,
  h6:last-child {
    margin-bottom: 0;
  }

  p {
    margin: 0 0 1em;
    font-size: ${constants.fontSize.p}rem;
  }

  p:first-child {
    margin-top: 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  li {
    font-size: ${constants.fontSize.p}rem;
  }

  a {
    color: ${({ theme }) => theme.link.text};
    text-decoration: none;
    font-size: inherit;
    font-weight: ${constants.fontWeight.normal};
    &:focus {
      outline-offset: 1px;
      outline: 2px solid ${({ theme }) => theme.focus};
    }
    &:hover {
      text-decoration: underline;
    }
  }
  div,
  span {
    font-size: inherit;
  }

  button,
  input,
  textarea,
  select {
    outline: none;
    font-family: ${({ theme }) => theme.font.fontFamily};
  }

  b,
  em {
    font-weight: ${constants.fontWeight.bold};
  }

  hr {
    border: none;
    border-top: 1px solid ${colors.gray_20};
    clear: both;
    margin-bottom: 1.25rem;
    width: 100%;
  }

  code,
  pre {
    font-family: ${constants.fontFamily.code};
    font-size: ${constants.fontSize.text_12}rem;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  code {
    display: inline-block;
    padding-left: 2px;
    padding-right: 2px;
    vertical-align: baseline;
    color: ${colors.gray_20};
  }

  pre {
    line-height: 18px;
    padding: 11px 1rem;
    white-space: pre-wrap;
    background: rgba(0, 0, 0, 0.05);
    color: ${({ theme }) => theme.page.body.text};
    border-radius: 3px;
    margin: 1rem 0;
  }

  /* override to address textfield zoom issue on mobile */
  input,
  select,
  textarea {
    font-size: ${deviceInfo.isMobile ? fontSize.base : fontSize.text_14}rem !important;
  }

  fieldset {
    min-width: 0;
    padding: 0;
    margin: 0;
    border: 0;
  }

  button:not(:disabled),
  [type='button']:not(:disabled),
  [type='reset']:not(:disabled),
  [type='submit']:not(:disabled) {
    cursor: pointer;
  }

  input[type='radio'],
  input[type='checkbox'] {
    box-sizing: border-box;
    padding: 0;
  }

  input {
    margin: 0;
  }

  &.noscroll {
    overflow: hidden;
    position: fixed;
  }

  strong,
  em,
  del,
  ins {
    font-size: inherit;
  }
`;

// Allow design system consumers to access font settings but control how and
// where they load the font.
export const KitFontUrl = 'https://fonts.googleapis.com/css?family=Roboto:300,400,500';

interface KitGlobalStylesProps {
  rootId: string;
}

export const KitGlobalStyles = createGlobalStyle<KitGlobalStylesProps>`
  html {
    ${KitHtmlStyles}
  }

  body {
    ${KitBodyStyles}
  }
  
  ::before,
  ::after {
    box-sizing: border-box;
  }

  #${(props) => props.rootId} {
    width: 100%;
    height: 100%;
  }
`;
